import React, { useEffect } from 'react'

import { setToast } from '@/entities/toast'
import { useGetApiSubscriptionTier } from '@/shared/apis/fastmile/generated/subscription-tier/subscription-tier'
import { usePostApiSubscriptionCreatePortalSession } from '@/shared/apis/fastmile/generated/subscription/subscription'
import { CreatePortalSessionRequestDto } from '@/shared/apis/fastmile/models/generated'
import { CurrentSubscriptionTier } from '@/shared/components/Subscription/CurrentSubscriptionTier'
import { useSubscription } from '@/shared/components/Subscription/SubscriptionContext'
import { useAppDispatch } from '@/shared/store'
import { Box, CircularProgress, styled, Typography } from '@mui/material'

import { getTierUIDetails } from './libs/getTierUIDetails'

export const ManageSubscriptionPage: React.FC = () => {
  const { subscription, refetch, isLoading: isSubscriptionLoading } = useSubscription()

  const dispatch = useAppDispatch()

  const { data: subscriptionTiersResponse, isLoading: tiersLoading } = useGetApiSubscriptionTier()
  const createPortalSession = usePostApiSubscriptionCreatePortalSession()

  const tiers = subscriptionTiersResponse?.data || []

  // Handle portal session returns
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const portalReturn = urlParams.get('portal_return')

    if (portalReturn === 'success') {
      const newUrl = window.location.pathname

      window.history.replaceState({}, '', newUrl)

      refetch()
    }
  }, [dispatch, refetch])

  const handleManageBilling = async () => {
    const portalRequest: CreatePortalSessionRequestDto = {
      returnUrl: `${window.location.origin}/subscription/manage?portal_return=success`,
    }

    const response = await createPortalSession.mutateAsync({ data: portalRequest })

    if (response?.data?.portalUrl) {
      window.location.href = response.data.portalUrl
    } else {
      dispatch(setToast({
        open: true,
        severity: 'error',
        title: 'Portal Error',
        content: 'Unable to open billing portal. Please try again.',
      }))
    }
  }

  const handleUpgrade = async () => {
    const portalRequest: CreatePortalSessionRequestDto = {
      returnUrl: `${window.location.origin}/subscription/manage?portal_return=success`,
    }

    const response = await createPortalSession.mutateAsync({ data: portalRequest })

    if (response?.data?.portalUrl) {
      window.location.href = response.data.portalUrl
    } else {
      dispatch(setToast({
        open: true,
        severity: 'error',
        title: 'Portal Error',
        content: 'Unable to open upgrade portal. Please try again.',
      }))
    }
  }

  if (!isSubscriptionLoading && (!subscription || subscription.tierName === 'Free')) {
    // Redirect to new subscription page if no subscription or free tier
    window.location.href = '/upgrade'

    return null
  }

  if (tiersLoading || !subscription) {
    return (
      <StyledContainer>
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <CircularProgress />
        </Box>
      </StyledContainer>
    )
  }

  const currentTier = tiers.find(tier => tier.name === subscription.tierName)
  const uiDetails = getTierUIDetails(subscription.tierName || '')

  if (!uiDetails) {
    return (
      <StyledContainer>
        <Typography variant="h6" color="error">
          Unable to load subscription details.
        </Typography>
      </StyledContainer>
    )
  }

  return (
    <StyledContainer>
      <StyledHeader>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Your Subscription
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph sx={{ fontWeight: 400 }}>
          Manage your current plan and billing information
        </Typography>
      </StyledHeader>

      <StyledContentWrapper>
        <CurrentSubscriptionTier
          subscription={subscription}
          tier={currentTier || null}
          description={uiDetails.description}
          features={uiDetails.features}
          onManageBilling={handleManageBilling}
          onUpgrade={handleUpgrade}
          isManageBillingLoading={createPortalSession.isPending}
          isUpgradeLoading={createPortalSession.isPending}
          currentVehicleUsage={subscription?.currentVehicleCount || 0}
          showUpgradeOption={subscription.tierName !== 'Enterprise'}
        />
      </StyledContentWrapper>
    </StyledContainer>
  )
}

const StyledContainer = styled(Box)`
  padding: ${(props) => props.theme.spacing(3, 2)};
  background-color: ${props => props.theme.palette.background.paper};
  min-height: 100vh;
  ${(props) => props.theme.breakpoints.up('md')} {
    padding: ${(props) => props.theme.spacing(4, 5)};
  }
`

const StyledHeader = styled(Box)`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing(5)};
`

const StyledContentWrapper = styled(Box)`
  max-width: ${props => props.theme.breakpoints.values.md}px;
  margin: 0 auto;
`